import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

export type ModoAutomatizacion = "aprobacion" | "auto";

export interface Automatizacion {
  id: number;
  tipo: string;
  nombre: string;
  descripcion: string | null;
  modo: ModoAutomatizacion;
  activo: boolean;
  config: Record<string, unknown> | null;
  ultima_ejecucion: string | null;
}

export interface Ejecucion {
  id: number;
  automatizacion_id: number;
  estado: "propuesta" | "aprobada" | "rechazada" | "ejecutada" | "error";
  titulo: string;
  resumen: string | null;
  payload: Record<string, unknown> | null;
  resultado: Record<string, unknown> | null;
  creado_en: string;
  automatizacion?: { id: number; nombre: string; tipo: string };
}

const BASE = "/automations";

export const autoKeys = {
  all: ["automations"] as const,
  rules: () => [...autoKeys.all, "rules"] as const,
  proposals: () => [...autoKeys.all, "proposals"] as const,
};

export function useRules() {
  return useQuery({
    queryKey: autoKeys.rules(),
    queryFn: async () => (await api.get<{ data: Automatizacion[] }>(BASE)).data.data,
  });
}

export function useProposals() {
  return useQuery({
    queryKey: autoKeys.proposals(),
    queryFn: async () =>
      (await api.get<{ pendientes: Ejecucion[]; historial: Ejecucion[]; briefing: Ejecucion | null }>(`${BASE}/proposals`)).data,
    refetchInterval: 60_000,
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Pick<Automatizacion, "activo" | "modo" | "config">> }) =>
      (await api.patch(`${BASE}/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: autoKeys.rules() }),
  });
}

export function useApproveProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.post(`${BASE}/proposals/${id}/approve`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: autoKeys.proposals() }),
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.post(`${BASE}/proposals/${id}/reject`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: autoKeys.proposals() }),
  });
}

export function useRunNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post(`${BASE}/run`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: autoKeys.proposals() }),
  });
}
